/**
 * Minicart Gift With Purchase picker
 * ---------------------------------------------------------------------------
 * Renders a "pick your free sampler" block in the minicart once the qualifying
 * subtotal is met, and adds the chosen $0.00 gift variant to the cart.
 *
 * The $50 minimum enforced here is PRESENTATION. It runs in the customer's
 * browser, and the gift is already $0.00 in the catalogue, so a hand-crafted
 * POST to /cart/add.js bypasses it entirely. That is an accepted trade — see
 * the architecture note in snippets/gwp-gift-picker.liquid. Enforcement lives
 * downstream in a Shopify Flow workflow that holds the fulfilment order when an
 * order looks abusive (gift present with paid subtotal under $30, or more than
 * one gift). Nothing ships until support releases it, which is what actually
 * prevents the loss.
 *
 * Integration pattern mirrors minicart-static-upsell.js:
 *   1. Read config from <script id="minicart-gwp-data">
 *   2. Watch the minicart container for open / update events
 *   3. Inject above the subtotal / totals block
 *   4. Write via /cart/add.js and /cart/change.js, then dispatch cart:updated
 *      AND window._.dispatch('getCart'). The bundled minicart does NOT re-fetch
 *      the cart when it opens, so that dispatch is required, not optional.
 *
 * INVARIANTS enforced on every cart read:
 *   - At most ONE gift line in the cart, at quantity 1.
 *   - No gift line at all once the qualifying subtotal drops below the
 *     threshold — including a line that was added by bypassing the picker.
 *   - A tile is never offered for a variant that is out of stock at render
 *     time or that failed to add at click time.
 *
 * THRESHOLD BASIS: pre-discount line totals (original_line_price) of non-gift
 * lines. Deliberate — a customer who earns "Buy 2 | Get 10% off" should not
 * lose their gift for having earned a discount.
 *
 * THREE THEME QUIRKS THIS FILE WORKS AROUND
 * -----------------------------------------
 * 1. /cart/add.js answers 422 for BOTH "sold out" and "Cannot find variant".
 *    The second means the gift product is not published to the Online Store
 *    channel — a setup fault, not a stock problem. Told apart in claim().
 *
 * 2. The bundled minicart renders <buy-more-save-more> on EVERY cart line,
 *    including the free gift. Its buttons set that line's quantity, so on the
 *    gift line "Buy 4+, Save 20%" hands over four samplers (verified: it really
 *    does, and reconcile() then snaps it back to 1). It is hidden by a CSS
 *    DESCENDANT rule on the decorated row rather than by a sweep, because the
 *    components are custom elements that upgrade AFTER our pass runs.
 *
 * 3. The FREE GIFT badge is a ::before, not a ::after. Rendered after the row's
 *    content it appears to label the NEXT cart line, which is worse than having
 *    no badge at all.
 */

(function () {
  'use strict';

  var CONFIG_ELEMENT_ID = 'minicart-gwp-data';
  var ROOT_CLASS = 'minicart__gwp';
  var STYLE_ID = 'minicart-gwp-styles';
  var LINE_CLASS = 'is-gwp-gift-line';

  // Anchors at the bottom of the cart, in priority order. We insert BEFORE the
  // first match so the gift block sits between the line items and the totals.
  // The static upsell is checked first so the gift lands ABOVE a paid upsell —
  // a free thing should not read as subordinate to something being sold.
  // None of these exist while the cart is empty, which is harmless.
  var INJECT_BEFORE_SELECTORS = [
    '.minicart__static-upsell',
    '.minicart__subtotal',
    '.minicart__totals',
    '.minicart__footer',
    '.minicart__bottom',
    '[class*="minicart__subtotal"]',
    '[class*="minicart__footer"]',
    '[class*="minicart__bottom"]'
  ];

  var config = null;
  var claimKey = '_gwp_claim';
  var giftMeta = {};
  var soldOutAtClick = {};
  var writing = false;
  var notice = '';
  var swapping = false;
  var configError = false;

  // ---------- helpers ----------

  function getConfig() {
    var el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[minicart-gwp] failed to parse config', e);
      return null;
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m];
    });
  }

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    return '$' + (Math.abs(cents) / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  /**
   * Gift lines are matched by PRODUCT ID first, and only then by the claim
   * property. That ordering matters: a line added by bypassing the picker
   * carries no property, and that is exactly the line reconcile() most needs to
   * be able to strip. The property is still stamped on adds we make, for Flow
   * conditions and auditing.
   */
  function isGiftLine(item) {
    if (!item) return false;
    if (config && item.product_id === config.product_id) return true;
    return !!(item.properties && item.properties[claimKey]);
  }

  function giftLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isGiftLine);
  }

  function giftLineIndexes(cart) {
    var out = [];
    if (!cart || !Array.isArray(cart.items)) return out;
    cart.items.forEach(function (item, i) { if (isGiftLine(item)) out.push(i); });
    return out;
  }

  /** Pre-discount total of everything that is NOT a gift. */
  function qualifyingCents(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isGiftLine(item)) return sum;
      var line = typeof item.original_line_price === 'number'
        ? item.original_line_price
        : (item.price || 0) * (item.quantity || 0);
      return sum + line;
    }, 0);
  }

  function availableGifts() {
    if (!config || !Array.isArray(config.gifts)) return [];
    return config.gifts.filter(function (g) {
      return g.in_stock && !soldOutAtClick[String(g.id)];
    });
  }

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/change failed (' + res.status + ')');
      return res.json();
    });
  }

  function broadcast(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    try {
      if (typeof window._ !== 'undefined' && typeof window._.dispatch === 'function') {
        window._.dispatch('getCart');
      }
    } catch (e) { /* no-op */ }
  }

  // ---------- invariant enforcement ----------

  /**
   * Termination: only writes when an invariant is violated. After a write the
   * cart satisfies the invariants, so the cart:updated it triggers leads to a
   * second pass that finds nothing to do. No loop.
   */
  function reconcile(cart) {
    var lines = giftLines(cart);
    var qualifies = qualifyingCents(cart) >= config.threshold_cents;
    var chain = Promise.resolve(cart);
    var wrote = false;

    if (!qualifies && lines.length) {
      lines.forEach(function (line) {
        chain = chain.then(function () { return changeLine(line.key, 0); });
      });
      notice = config.copy.removed_notice;
      wrote = true;
    } else if (qualifies && lines.length > 1) {
      lines.slice(1).forEach(function (line) {
        chain = chain.then(function () { return changeLine(line.key, 0); });
      });
      wrote = true;
    } else if (qualifies && lines.length === 1 && lines[0].quantity > 1) {
      chain = chain.then(function () { return changeLine(lines[0].key, 1); });
      wrote = true;
    }

    if (!wrote) return Promise.resolve({ cart: cart, wrote: false });
    return chain.then(function (updated) { return { cart: updated, wrote: true }; });
  }

  // ---------- rendering ----------

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      '.' + ROOT_CLASS + '{margin:0 30px 16px;padding:14px;border:1px solid var(--text-color,#003452);',
      'border-radius:8px;background:#fff;font-family:inherit;color:var(--text-color,#003452);}',
      '.' + ROOT_CLASS + '--locked{border-style:dashed;opacity:.9;}',
      '.' + ROOT_CLASS + '__heading{margin:0 0 2px;font-size:11px;font-weight:700;letter-spacing:.5px;',
      'text-transform:uppercase;}',
      '.' + ROOT_CLASS + '__sub{margin:0 0 10px;font-size:12px;line-height:1.4;opacity:.75;}',
      '.' + ROOT_CLASS + '__notice{margin:0 0 10px;font-size:12px;font-weight:600;color:#C04A2B;}',
      '.' + ROOT_CLASS + '__progress{height:4px;border-radius:2px;background:rgba(0,52,82,.12);',
      'overflow:hidden;margin-top:8px;}',
      '.' + ROOT_CLASS + '__progress span{display:block;height:100%;background:#C04A2B;',
      'border-radius:2px;transition:width .3s ease;}',
      '.' + ROOT_CLASS + '__grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}',
      '.' + ROOT_CLASS + '__tile{display:flex;flex-direction:column;align-items:center;gap:6px;',
      'padding:10px 8px;border:1px solid rgba(0,52,82,.25);border-radius:6px;background:#fff;',
      'cursor:pointer;text-align:center;font-family:inherit;color:inherit;transition:border-color .15s,box-shadow .15s;}',
      '.' + ROOT_CLASS + '__tile:hover{border-color:#003452;box-shadow:0 1px 6px rgba(0,52,82,.12);}',
      '.' + ROOT_CLASS + '__tile[disabled]{opacity:.45;cursor:not-allowed;}',
      '.' + ROOT_CLASS + '__tile img{width:52px;height:52px;object-fit:contain;background:#f6f6f6;border-radius:4px;}',
      '.' + ROOT_CLASS + '__tile-title{font-size:11px;font-weight:600;line-height:1.25;',
      'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}',
      '.' + ROOT_CLASS + '__tile-value{font-size:10px;opacity:.6;text-decoration:line-through;}',
      '.' + ROOT_CLASS + '__tile-cta{font-size:10px;font-weight:700;text-transform:uppercase;',
      'letter-spacing:.4px;color:#C04A2B;}',
      '.' + ROOT_CLASS + '__claimed{display:flex;align-items:center;gap:12px;}',
      '.' + ROOT_CLASS + '__claimed img{width:52px;height:52px;object-fit:contain;background:#f6f6f6;border-radius:4px;}',
      '.' + ROOT_CLASS + '__claimed-info{flex:1 1 auto;min-width:0;}',
      '.' + ROOT_CLASS + '__claimed-title{margin:0;font-size:13px;font-weight:600;line-height:1.3;}',
      '.' + ROOT_CLASS + '__badge{display:inline-block;margin-top:3px;padding:2px 6px;border-radius:3px;',
      'background:#C04A2B;color:#fff;font-size:9px;font-weight:700;letter-spacing:.5px;}',
      '.' + ROOT_CLASS + '__was{margin-left:6px;font-size:10px;opacity:.6;text-decoration:line-through;}',
      '.' + ROOT_CLASS + '__change{flex-shrink:0;padding:6px 10px;border:1px solid var(--text-color,#003452);',
      'background:transparent;border-radius:6px;font-size:10px;font-weight:700;text-transform:uppercase;',
      'letter-spacing:.4px;cursor:pointer;color:inherit;font-family:inherit;}',
      '.' + ROOT_CLASS + '__change:hover{background:rgba(0,52,82,.06);}',
      // Quirk 2
      '.' + LINE_CLASS + ' buy-more-save-more{display:none!important;}',
      // Quirk 3
      '.' + LINE_CLASS + '::before{content:"FREE GIFT";display:inline-block;',
      'margin:0 0 4px 30px;padding:2px 6px;border-radius:3px;background:#C04A2B;color:#fff;',
      'font-size:9px;font-weight:700;letter-spacing:.5px;font-family:inherit;}',
      '@media (max-width:420px){.' + ROOT_CLASS + '{margin:0 16px 16px;}',
      '.' + LINE_CLASS + '::before{margin-left:16px;}}'
    ].join('');
    document.head.appendChild(style);
  }

  function findInjectionTarget() {
    for (var i = 0; i < INJECT_BEFORE_SELECTORS.length; i++) {
      var el = document.querySelector(INJECT_BEFORE_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  /** Mark the gift cart row, anchored by cart index. */
  function decorateGiftCartLines(cart) {
    var rows = document.querySelectorAll('.minicart-product');
    if (!rows.length) return;
    var giftIdx = {};
    giftLineIndexes(cart).forEach(function (i) { giftIdx[i] = true; });
    for (var i = 0; i < rows.length; i++) {
      rows[i].classList.toggle(LINE_CLASS, !!giftIdx[i]);
    }
  }

  function tileHtml(gift) {
    var valueLine = gift.value > 0
      ? '<span class="' + ROOT_CLASS + '__tile-value">' + escapeHtml(money(gift.value)) + '</span>'
      : '';
    var img = gift.image
      ? '<img src="' + escapeHtml(gift.image) + '" alt="' + escapeHtml(gift.title) + '" loading="lazy" width="52" height="52">'
      : '';
    return '' +
      '<button type="button" class="' + ROOT_CLASS + '__tile" data-gwp-claim="' + escapeHtml(gift.id) + '">' +
        img +
        '<span class="' + ROOT_CLASS + '__tile-title">' + escapeHtml(gift.title) + '</span>' +
        valueLine +
        '<span class="' + ROOT_CLASS + '__tile-cta">' + escapeHtml(config.copy.button_label) + '</span>' +
      '</button>';
  }

  function buildBlock(cart) {
    if (configError) return null;

    var qualifying = qualifyingCents(cart);
    var qualifies = qualifying >= config.threshold_cents;
    var claimed = giftLines(cart)[0] || null;
    var offerable = availableGifts();

    if (!offerable.length && !claimed) return null;

    var block = document.createElement('div');
    block.className = ROOT_CLASS;

    var noticeHtml = notice
      ? '<p class="' + ROOT_CLASS + '__notice">' + escapeHtml(notice) + '</p>'
      : '';

    if (!qualifies) {
      if (!config.show_progress && !notice) return null;
      var remaining = config.threshold_cents - qualifying;
      var pct = Math.max(0, Math.min(100, (qualifying / config.threshold_cents) * 100));
      var msg = String(config.copy.progress_message).replace('[remaining]', money(remaining));
      block.className += ' ' + ROOT_CLASS + '--locked';
      block.innerHTML =
        '<p class="' + ROOT_CLASS + '__heading">' + escapeHtml(msg) + '</p>' +
        noticeHtml +
        '<div class="' + ROOT_CLASS + '__progress"><span style="width:' + pct.toFixed(1) + '%"></span></div>';
      return block;
    }

    if (claimed && !swapping) {
      var meta = giftMeta[String(claimed.variant_id)] || {};
      var image = meta.image || claimed.image;
      var label = meta.title || claimed.variant_title || claimed.title;
      var was = meta.value > 0
        ? '<span class="' + ROOT_CLASS + '__was">' + escapeHtml(money(meta.value)) + '</span>'
        : '';
      var imgHtml = image
        ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(label) + '" width="52" height="52">'
        : '';
      var changeBtn = offerable.length > 1
        ? '<button type="button" class="' + ROOT_CLASS + '__change" data-gwp-swap="1">' +
            escapeHtml(config.copy.change_label) + '</button>'
        : '';
      block.innerHTML =
        '<p class="' + ROOT_CLASS + '__heading">' + escapeHtml(config.copy.claimed_heading) + '</p>' +
        noticeHtml +
        '<div class="' + ROOT_CLASS + '__claimed">' +
          imgHtml +
          '<div class="' + ROOT_CLASS + '__claimed-info">' +
            '<p class="' + ROOT_CLASS + '__claimed-title">' + escapeHtml(label) + '</p>' +
            '<span class="' + ROOT_CLASS + '__badge">' + escapeHtml(config.copy.free_label) + '</span>' + was +
          '</div>' +
          changeBtn +
        '</div>';
      return block;
    }

    block.innerHTML =
      '<p class="' + ROOT_CLASS + '__heading">' + escapeHtml(config.copy.unlocked_heading) + '</p>' +
      '<p class="' + ROOT_CLASS + '__sub">' + escapeHtml(config.copy.unlocked_subheading) + '</p>' +
      noticeHtml +
      '<div class="' + ROOT_CLASS + '__grid">' +
        offerable.map(tileHtml).join('') +
      '</div>';
    return block;
  }

  // ---------- actions ----------

  function claim(variantId, button) {
    if (writing) return;
    writing = true;
    var restore = button.innerHTML;
    button.disabled = true;

    var properties = {};
    properties[claimKey] = '1';

    fetchCart()
      .then(function (cart) {
        var existing = giftLines(cart)[0];
        if (!existing) return null;
        return changeLine(existing.key, 0);
      })
      .then(function () {
        // The claim property is not what makes this a gift — the product is.
        // It is stamped so Flow can key on it and so support can tell a picker
        // claim apart from a hand-crafted add. The leading underscore keeps it
        // out of the customer-facing cart and order confirmation.
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: properties }] })
        });
      })
      .then(function (res) {
        if (res.status === 422) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            var desc = String(body.description || '');
            var err = new Error(desc || 'cart/add rejected');
            if (/cannot find variant|does not exist|not found/i.test(desc)) {
              err.misconfigured = true;
            } else {
              err.outOfStock = true;
            }
            throw err;
          });
        }
        if (!res.ok) throw new Error('cart/add failed (' + res.status + ')');
        return res.json();
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        swapping = false;
        notice = '';
        writing = false;
        broadcast(cart);
        render(cart);
      })
      .catch(function (err) {
        writing = false;
        button.disabled = false;
        button.innerHTML = restore;

        if (err && err.misconfigured) {
          configError = true;
          console.error(
            '[minicart-gwp] gift variant ' + variantId + ' is not purchasable. The Free ' +
            'Sampler product must be published to the Online Store sales channel — status ' +
            'UNLISTED is not sufficient on its own. Hiding the offer.'
          );
          fetchCart().then(render).catch(function () { render(null); });
          return;
        }
        if (err && err.outOfStock) {
          soldOutAtClick[String(variantId)] = true;
          notice = config.copy.soldout_notice;
          console.warn('[minicart-gwp] variant ' + variantId + ' out of stock at click time');
          fetchCart().then(render).catch(function () { render(null); });
          return;
        }
        console.error('[minicart-gwp]', err);
      });
  }

  function beginSwap() {
    swapping = true;
    notice = '';
    fetchCart().then(render).catch(function () { render(null); });
  }

  // ---------- injection ----------

  function render(cart) {
    if (!config || !config.enabled) return;

    var proceed = function (c) {
      var existing = document.querySelectorAll('.' + ROOT_CLASS);
      for (var i = 0; i < existing.length; i++) existing[i].remove();

      decorateGiftCartLines(c);

      var target = findInjectionTarget();
      if (!target) return;

      var block = buildBlock(c);
      if (!block) return;

      target.parentNode.insertBefore(block, target);

      var tiles = block.querySelectorAll('[data-gwp-claim]');
      for (var j = 0; j < tiles.length; j++) {
        (function (btn) {
          btn.addEventListener('click', function () {
            claim(btn.getAttribute('data-gwp-claim'), btn);
          });
        })(tiles[j]);
      }
      var swapBtn = block.querySelector('[data-gwp-swap]');
      if (swapBtn) swapBtn.addEventListener('click', beginSwap);
    };

    if (cart) { proceed(cart); return; }
    fetchCart().then(proceed).catch(function (e) {
      console.error('[minicart-gwp] cart fetch failed', e);
    });
  }

  function pass() {
    if (!config || !config.enabled || writing) return;
    fetchCart()
      .then(function (cart) {
        return reconcile(cart).then(function (result) {
          if (result.wrote) broadcast(result.cart);
          render(result.cart);
        });
      })
      .catch(function (e) { console.error('[minicart-gwp] pass failed', e); });
  }

  function attemptInjection() {
    if (!config || !config.enabled) return;
    setTimeout(pass, 120);
    setTimeout(pass, 500);
  }

  function observeMinicart() {
    var container = document.querySelector('.minicart__container, #minicart');
    if (!container) {
      setTimeout(observeMinicart, 500);
      return;
    }
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'class') return;
        var c = m.target;
        if (c.classList.contains('minicart__container--open') || c.classList.contains('is-open')) {
          attemptInjection();
        }
      });
    });
    observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    attemptInjection();
  }

  function init() {
    config = getConfig();
    if (!config || !config.enabled) return;

    claimKey = config.claim_attribute || '_gwp_claim';
    config.copy = config.copy || {};
    if (!config.copy.removed_notice) {
      config.copy.removed_notice = 'Your free sampler was removed — the cart no longer qualifies.';
    }

    (config.gifts || []).forEach(function (g) { giftMeta[String(g.id)] = g; });

    ensureStyles();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeMinicart);
    } else {
      observeMinicart();
    }

    document.addEventListener('cart:updated', attemptInjection);
    document.addEventListener('cart:refresh', attemptInjection);
    document.addEventListener('minicart:opened', attemptInjection);
    document.addEventListener('minicart:updated', attemptInjection);
  }

  init();
})();